// Port of `CharacterStudy.answers(in:)` (SuiteModel/CharacterStudy.swift): what a character's study page says, prompt by
// prompt — each bold label ("**Wants:**") and the plain text after it, up to the next label. The study page itself (made
// by `ensureCharacterStudies`) is the core's `StoreEditor`.
import { blockPlainText, parse } from '../proseMarkdown.ts';
import { trimCFWhitespace, isCFWhitespaceOrNewline } from '../unicode.ts';

export interface StudyAnswer { label: string; text: string }

function trimAll(s: string): string {
  const cps = Array.from(s);
  let a = 0, b = cps.length;
  while (a < b && isCFWhitespaceOrNewline(cps[a].codePointAt(0)!)) a++;
  while (b > a && isCFWhitespaceOrNewline(cps[b - 1].codePointAt(0)!)) b--;
  return cps.slice(a, b).join('');
}

const plain = (markdown: string) => trimCFWhitespace(parse(markdown).map(blockPlainText).join('\n\n'));

/** `CharacterStudy.answers(in:)`: the answered prompts, in order (a label with nothing after it is left out). */
export function studyAnswers(text: string): StudyAnswer[] {
  // Swift Regex `/^\*\*(.+?)\*\*\s*(.*)$/`: `.` stops at a newline, `^`/`$` are the block's ends.
  const label = /^\*\*(.+?)\*\*\s*(.*)$/u;
  const result: StudyAnswer[] = [];
  let current: StudyAnswer | null = null;
  const finish = () => {
    if (current && trimCFWhitespace(current.text) !== '') result.push(current);
    current = null;
  };
  for (const block of text.split('\n\n')) {
    const trimmed = trimAll(block);
    if (trimmed === '') continue;
    const match = label.exec(trimmed);
    if (match) {
      finish();
      let name = trimCFWhitespace(match[1]);
      let rest = match[2];
      // "**Wants:**" and "**Wants**:" both label a prompt.
      if (name.endsWith(':') || name.endsWith('.')) name = name.slice(0, -1);
      if (rest.startsWith(':')) rest = rest.slice(1);
      current = { label: name, text: plain(rest) };
    } else if (current) {
      const c: StudyAnswer = current;
      c.text += (c.text === '' ? '' : '\n\n') + plain(trimmed);
    }
  }
  finish();
  return result;
}
